import { useState } from 'react';
import { Check } from 'lucide-react';
import { useOnboardingStatus, useOnboardingIntegrations } from '../../hooks/useOnboarding';
import { StepDependencies } from './StepDependencies';
import { StepCompanyProfile } from './StepCompanyProfile';
import { StepCompany } from './StepCompany';
import { StepExcel } from './StepExcel';
import { StepSuppliers } from './StepSuppliers';
import { StepTasks } from './StepTasks';
import { StepBank } from './StepBank';

// Setări → Onboarding → „Pornesc de la zero”: the setup wizard (CLE-131,
// extended CLE-130, CLE-229/230), moved here from /onboarding. A resumable
// 7-step flow whose step indicator is driven by GET /api/onboarding/status
// (data steps) and GET /api/onboarding/integrations (dependencies step); each
// step can be skipped.

type StepKey = 'profile' | 'deps' | 'company' | 'excel' | 'suppliers' | 'tasks' | 'bank';

const STEPS: { key: StepKey; n: number; label: string }[] = [
  { key: 'profile', n: 1, label: 'Compania ta' },
  { key: 'deps', n: 2, label: 'Integrări' },
  { key: 'company', n: 3, label: 'Clienți' },
  { key: 'excel', n: 4, label: 'Excel' },
  { key: 'suppliers', n: 5, label: 'Furnizori' },
  { key: 'tasks', n: 6, label: 'Task-uri' },
  { key: 'bank', n: 7, label: 'Extras bancar' },
];

type StepDoneMap = Record<StepKey, boolean>;

function StepIndicator({
  current,
  steps,
  onJump,
}: {
  current: number;
  steps: StepDoneMap;
  onJump: (i: number) => void;
}) {
  return (
    <ol className="flex items-center overflow-x-auto">
      {STEPS.map((step, i) => {
        const done = steps[step.key];
        const active = i === current;
        return (
          <li key={step.key} className="flex flex-1 items-center last:flex-none">
            <button
              type="button"
              onClick={() => onJump(i)}
              className="flex items-center gap-2 text-left"
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
                  done
                    ? 'border-green-500 bg-green-500 text-white'
                    : active
                      ? 'border-brand-600 bg-brand-600 text-white'
                      : 'border-gray-300 bg-white text-gray-500'
                }`}
              >
                {done ? <Check className="h-4 w-4" /> : step.n}
              </span>
              <span
                className={`hidden text-sm font-medium sm:inline ${
                  active ? 'text-brand-700' : done ? 'text-green-700' : 'text-gray-500'
                }`}
              >
                {step.label}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <span className={`mx-2 h-0.5 flex-1 ${done ? 'bg-green-300' : 'bg-gray-200'}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function StartWizard() {
  const statusQuery = useOnboardingStatus();
  const integrationsQuery = useOnboardingIntegrations();
  const [current, setCurrent] = useState(0);

  const status = statusQuery.data;

  // The dependencies step counts as "done" once no required integration is
  // still missing its env vars (summary.blocking === 0). Optional integrations
  // never block completion.
  const depsDone = (integrationsQuery.data?.summary.blocking ?? 1) === 0;

  const stepsDone: StepDoneMap | null = status
    ? {
        profile: status.steps.companyProfile,
        deps: depsDone,
        company: status.steps.company,
        excel: status.steps.excel,
        suppliers: status.steps.suppliers,
        tasks: status.steps.tasks,
        bank: status.steps.bank,
      }
    : null;

  function goTo(i: number) {
    setCurrent(Math.max(0, Math.min(STEPS.length - 1, i)));
  }

  return (
    <div className="w-full">
      <p className="text-sm text-gray-500">
        Conectează integrările și importă datele afacerii tale în 7 pași. Poți sări peste orice pas
        și reveni mai târziu.
      </p>

      {statusQuery.isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă progresul…</p>}
      {statusQuery.isError && (
        <p className="mt-6 text-sm text-red-600">
          Nu s-a putut încărca progresul asistentului
          {statusQuery.error instanceof Error ? `: ${statusQuery.error.message}` : ''}.
        </p>
      )}

      {status && stepsDone && (
        <>
          <div className="mt-6">
            <StepIndicator current={current} steps={stepsDone} onJump={goTo} />
          </div>

          <div className="mt-8">
            {current === 0 && <StepCompanyProfile />}
            {current === 1 && <StepDependencies />}
            {current === 2 && <StepCompany status={status} />}
            {current === 3 && <StepExcel />}
            {current === 4 && <StepSuppliers status={status} />}
            {current === 5 && <StepTasks status={status} />}
            {current === 6 && <StepBank />}
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={() => goTo(current - 1)}
              disabled={current === 0}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              Înapoi
            </button>
            <div className="flex items-center gap-2">
              {current < STEPS.length - 1 && (
                <button
                  type="button"
                  onClick={() => goTo(current + 1)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Sari peste
                </button>
              )}
              {current < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={() => goTo(current + 1)}
                  className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
                >
                  Continuă
                </button>
              ) : (
                <span className="text-sm font-medium text-green-700">Ultimul pas</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
